/**
 * Shared mid-panel filters applied to every inventory-domain exception
 * config that ships product rows (product_pricing / stock_management /
 * product_setup). Mirrors the precedent set by
 * ``sharedSalesFilters.ts``.
 *
 * Lenses:
 *   1. Main Category   - multiselect, OR within the set.
 *   2. Type            - multiselect (variant / composite / simple),
 *                        OR within the set.
 *   3. Active          - tri-state select (all / active / inactive).
 *   4. Hide zero-stock - toggle.
 *   5. Search          - case-insensitive name/SKU contains.
 *
 * All five resolve server-side via ``buildInventoryFilterParams`` so
 * the Open/Closed pill counts (lifecycle envelope) stay coherent with
 * the table. ``DataViewTable`` already treats select/toggle/search
 * filter defs without a ``predicate`` callback as server-side - the
 * defs below omit ``predicate``/``getValue`` deliberately for that
 * reason.
 *
 * Adding the filters to a config:
 *
 *   import { sharedInventoryFilters, buildInventoryFilterParams }
 *     from '../shared/sharedInventoryFilters'
 *
 *   filters: sharedInventoryFilters,
 *   buildFetchParams: (urlState) => ({
 *     ...buildInventoryFilterParams(urlState.filterValues),
 *   }),
 */
import type { ExceptionFilterDef } from '../types'
import {
  formatMainCategory,
  isMissingCategory,
  NO_CATEGORY_LABEL,
  NO_CATEGORY_VALUE,
} from '../../../utils/productCategory'

export const SHARED_INVENTORY_FILTER_KEYS = {
  mainCategory: 'category_root_name',
  productType: 'product_type',
  active: 'active',
  hasStock: 'has_stock',
  search: 'search',
} as const

export const sharedInventoryFilters: ExceptionFilterDef[] = [
  {
    key: SHARED_INVENTORY_FILTER_KEYS.mainCategory,
    type: 'multiselect',
    label: 'Main Category',
    extractOptions: (rows) => {
      const counts = new Map<string, number>()
      for (const row of rows) {
        const raw = row.category_root_name
        const key = isMissingCategory(raw) ? NO_CATEGORY_VALUE : String(raw)
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
      return Array.from(counts.entries())
        .sort(([a], [b]) => {
          if (a === NO_CATEGORY_VALUE) return 1
          if (b === NO_CATEGORY_VALUE) return -1
          return a.localeCompare(b)
        })
        .map(([value, count]) => ({
          value,
          label:
            value === NO_CATEGORY_VALUE
              ? NO_CATEGORY_LABEL
              : formatMainCategory(value),
          count,
          variant: value === NO_CATEGORY_VALUE ? ('subtle' as const) : undefined,
        }))
    },
  },
  {
    key: SHARED_INVENTORY_FILTER_KEYS.productType,
    type: 'multiselect',
    label: 'Type',
    options: [
      { value: 'simple', label: 'Standard' },
      { value: 'composite', label: 'Composites' },
      { value: 'variant', label: 'Variants' },
    ],
  },
  {
    key: SHARED_INVENTORY_FILTER_KEYS.active,
    type: 'select',
    label: 'Status',
    defaultValue: 'all',
    options: [
      { value: 'all', label: 'All statuses' },
      { value: 'active', label: 'Active only' },
      { value: 'inactive', label: 'Inactive only' },
    ],
  },
  {
    key: SHARED_INVENTORY_FILTER_KEYS.hasStock,
    type: 'toggle',
    label: 'Hide zero-stock',
    defaultEnabled: false,
  },
  {
    key: SHARED_INVENTORY_FILTER_KEYS.search,
    type: 'search',
    label: 'Search',
    placeholder: 'Search product name or SKU',
  },
]

/**
 * Translate the shared-inventory filter values into backend query
 * params. Empty / default values are omitted so URLs + request
 * payloads stay minimal.
 *
 * Backend param keys mirror the route signatures on
 * ``product_pricing.py`` / ``stock_management.py`` / ``product_setup.py``
 * (``main_category`` / ``product_type`` / ``active`` / ``has_stock`` /
 * ``search``).
 */
export const buildInventoryFilterParams = (
  filterValues: Record<string, unknown> | undefined,
): Record<string, string | string[] | boolean> => {
  const params: Record<string, string | string[] | boolean> = {}
  if (!filterValues) return params

  const category = filterValues[SHARED_INVENTORY_FILTER_KEYS.mainCategory]
  if (Array.isArray(category) && category.length > 0) {
    params.main_category = (category as string[]).filter(
      (v) => v && v !== NO_CATEGORY_VALUE,
    )
    // Drop the key entirely if the only selection was "No category" -
    // backend has no representation for that yet (it'd need a NULL
    // category filter). Keep this as a follow-up if needed.
    if ((params.main_category as string[]).length === 0) {
      delete params.main_category
    }
  }

  const productType = filterValues[SHARED_INVENTORY_FILTER_KEYS.productType]
  if (Array.isArray(productType) && productType.length > 0) {
    params.product_type = productType as string[]
  }

  const active = filterValues[SHARED_INVENTORY_FILTER_KEYS.active]
  if (typeof active === 'string' && active && active !== 'all') {
    params.active = active
  }

  if (filterValues[SHARED_INVENTORY_FILTER_KEYS.hasStock] === true) {
    params.has_stock = true
  }

  const search = filterValues[SHARED_INVENTORY_FILTER_KEYS.search]
  if (typeof search === 'string' && search.trim().length >= 2) {
    params.search = search.trim()
  }

  return params
}
